import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, Undo2, Trash2, Pencil, RotateCcw, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import api, { formatApiErrorDetail } from '../../lib/api';
import { toast } from 'sonner';

const DeleteButton = ({ eventId, onDelete }) => {
  const [arming, setArming] = useState(false);

  useEffect(() => {
    if (!arming) return undefined;
    const t = setTimeout(() => setArming(false), 4000);
    return () => clearTimeout(t);
  }, [arming]);

  return arming ? (
    <Button
      size="sm"
      onClick={() => onDelete(eventId)}
      data-testid={`event-delete-confirm-${eventId}`}
      className="h-8 bg-red-600 hover:bg-red-700 text-white"
    >
      <Trash2 className="w-4 h-4 mr-1" /> Really Delete?
    </Button>
  ) : (
    <Button
      size="sm"
      variant="outline"
      onClick={() => setArming(true)}
      data-testid={`event-delete-${eventId}`}
      className="h-8 border-red-300 text-red-600 hover:bg-red-50"
    >
      <Trash2 className="w-4 h-4 mr-1" /> Delete
    </Button>
  );
};

const Row = ({ event, onConfirm, onRevert, onDelete, onSave }) => {
  const [date, setDate] = useState(event.date);
  const [time, setTime] = useState(event.time || '');
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(event.title);
  const [location, setLocation] = useState(event.location || '');
  const [saving, setSaving] = useState(false);
  const isCommunity = event.event_type === 'community';
  const tentative = !!event.tentative;

  const handleConfirm = async () => {
    setSaving(true);
    await onConfirm(event.id, date, time);
    setSaving(false);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(event.id, { title, location, date, time });
    setSaving(false);
    setEditing(false);
  };

  return (
    <div
      data-testid={`community-event-row-${event.id}`}
      className={`rounded-lg border p-3 ${tentative ? 'border-gray-200 bg-gray-50' : 'border-[#bd8cae]/50 bg-[#bd8cae]/10'}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium text-[#3c4a2a] flex-1 min-w-[180px]">{event.title}</p>
        {!isCommunity ? (
          <Badge className="bg-[#5d6b3c] text-white" data-testid={`community-event-status-${event.id}`}>Club Event</Badge>
        ) : tentative ? (
          <Badge variant="secondary" className="bg-gray-200 text-gray-600" data-testid={`community-event-status-${event.id}`}>Tentative</Badge>
        ) : (
          <Badge className="bg-[#bd8cae] text-white" data-testid={`community-event-status-${event.id}`}>Confirmed</Badge>
        )}
      </div>
      <p className="text-xs text-[#3c4a2a]/60 mt-1">{event.date} • {event.time} • {event.location}</p>

      {editing ? (
        <div className="mt-2 space-y-2" data-testid={`event-edit-form-${event.id}`}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Event title" data-testid={`event-edit-title-${event.id}`} className="h-8 text-sm" />
          <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location" data-testid={`event-edit-location-${event.id}`} className="h-8 text-sm" />
          <div className="flex flex-wrap gap-2">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`event-edit-date-${event.id}`} className="w-40 h-8 text-sm" />
            <Input value={time} onChange={(e) => setTime(e.target.value)} placeholder="Time" data-testid={`event-edit-time-${event.id}`} className="w-48 h-8 text-sm" />
            <Button size="sm" onClick={handleSave} disabled={saving || !title.trim() || !date} data-testid={`event-edit-save-${event.id}`} className="h-8 bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white">
              Save Changes
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid={`event-edit-cancel-${event.id}`} className="h-8 text-[#3c4a2a]/60">
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          {tentative && (
            <>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid={`community-event-date-${event.id}`}
                className="w-40 h-8 text-sm"
              />
              <Input
                value={time}
                onChange={(e) => setTime(e.target.value)}
                placeholder="Final time, e.g. 10:00 AM - 2:00 PM"
                data-testid={`community-event-time-${event.id}`}
                className="w-56 h-8 text-sm"
              />
              <Button
                size="sm"
                onClick={handleConfirm}
                disabled={saving || !date}
                data-testid={`community-event-confirm-${event.id}`}
                className="h-8 bg-[#a3688f] hover:bg-[#8a567a] text-white"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Approve Event
              </Button>
            </>
          )}
          {isCommunity && !tentative && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRevert(event.id)}
              data-testid={`community-event-revert-${event.id}`}
              className="h-8 border-gray-300 text-gray-600"
            >
              <Undo2 className="w-4 h-4 mr-1" />
              Mark Tentative Again
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
            data-testid={`event-edit-${event.id}`}
            className="h-8 border-[#5d6b3c]/40 text-[#5d6b3c] hover:bg-[#5d6b3c]/10"
          >
            <Pencil className="w-4 h-4 mr-1" /> Edit
          </Button>
          <DeleteButton eventId={event.id} onDelete={onDelete} />
        </div>
      )}
    </div>
  );
};

export const CommunityEventsAdmin = () => {
  const [events, setEvents] = useState([]);
  const [deleted, setDeleted] = useState([]);
  const [filter, setFilter] = useState('all');

  const load = useCallback(() => {
    api.get('/events').then((res) => setEvents(res.data)).catch(() => {});
    api.get('/admin/deleted-events').then((res) => setDeleted(res.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleConfirm = async (id, date, time) => {
    try {
      await api.put(`/admin/events/${id}`, { tentative: false, date, time: time || undefined });
      toast.success('Event approved — it now shows in pink with its final date.');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleRevert = async (id) => {
    try {
      await api.put(`/admin/events/${id}`, { tentative: true });
      toast.success('Event marked tentative again (gray).');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success('Event deleted — find it under Recently Deleted if you need it back.');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleSave = async (id, fields) => {
    try {
      await api.put(`/admin/events/${id}`, fields);
      toast.success('Event updated.');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const handleRestore = async (id) => {
    try {
      const res = await api.post(`/admin/deleted-events/${id}/restore`);
      toast.success(res.data.message || 'Event restored to the calendar.');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (!events.length && !deleted.length) return null;
  const shown = filter === 'all' ? events
    : filter === 'club' ? events.filter((e) => e.event_type !== 'community')
    : filter === 'tentative' ? events.filter((e) => e.tentative)
    : filter === 'confirmed' ? events.filter((e) => e.event_type === 'community' && !e.tentative)
    : [];
  const tentativeCount = events.filter((e) => e.tentative).length;

  const filters = [
    ['all', `All (${events.length})`],
    ['club', 'Club Events'],
    ['tentative', `Tentative (${tentativeCount})`],
    ['confirmed', 'Confirmed Community'],
    ['deleted', `Recently Deleted (${deleted.length})`],
  ];

  return (
    <Card className="bg-white border border-[#bd8cae]/30 shadow-sm mb-6" data-testid="community-events-admin">
      <CardHeader>
        <CardTitle className="font-serif text-xl text-[#3c4a2a]">Manage Calendar Events — Approve, Edit or Delete</CardTitle>
        <p className="text-sm text-[#3c4a2a]/60">
          Approve a gray tentative event to turn it pink, edit any event's title, location or date, or delete it — deleted events can be restored from Recently Deleted.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          {filters.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              data-testid={`event-manager-filter-${key}`}
              className={`rounded-full px-3 py-1 text-xs border transition-colors ${
                filter === key ? 'bg-[#5d6b3c] border-[#5d6b3c] text-white' : 'border-[#bd8cae]/40 text-[#3c4a2a]/70 hover:bg-[#bd8cae]/10'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {filter === 'deleted' ? (
            <>
              {deleted.map((e) => (
                <div key={e.id} data-testid={`deleted-event-row-${e.id}`} className="rounded-lg border border-red-200 bg-red-50/50 p-3 flex flex-wrap items-center gap-2">
                  <div className="flex-1 min-w-[180px]">
                    <p className="font-medium text-[#3c4a2a]">{e.title}</p>
                    <p className="text-xs text-[#3c4a2a]/60">{e.date} • {e.time} • {e.location}</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleRestore(e.id)}
                    data-testid={`event-restore-${e.id}`}
                    className="h-8 bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" /> Restore to Calendar
                  </Button>
                </div>
              ))}
              {!deleted.length && <p className="text-sm text-[#3c4a2a]/50" data-testid="deleted-events-empty">Nothing in the recycle bin — no events have been deleted.</p>}
            </>
          ) : (
            <>
              {shown.map((e) => (
                <Row key={e.id} event={e} onConfirm={handleConfirm} onRevert={handleRevert} onDelete={handleDelete} onSave={handleSave} />
              ))}
              {!shown.length && <p className="text-sm text-[#3c4a2a]/50">No events in this filter.</p>}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
